// Closed-win derivation for the Cases Home results lifecycle.

// Two result buckets; payment-plan / verified+paid need a resolution-outcome
// field we don't have yet, so they collapse into these.
export type WinBucket = "saved" | "verified";

// Closure-reason values that count as a positive resolution. The schema field is
// free-text with no enum, so the gate lives here; "Resolved" is the value staff
// set today.
export const RESOLVED_CLOSURE_REASONS: readonly string[] = ["Resolved"];

// The one UserClosureReason enum value that counts as a win; the other reasons
// (no longer need help, moved case) aren't outcomes to surface.
export const USER_RESOLVED_CLOSURE_REASON = "RESOLVED";

export type ClosedCaseLike = {
  id: string;
  title?: string | null;
  caseType?: string | null;
  caseManagementClosureReason?: string | null;
  /** Structured reason when the owner closed the case themselves. */
  userClosureReason?: string | null;
  /** ISO close timestamp; drives most-recent-first slot selection. */
  closedAt?: string | null;
  /** Decimal serialized as a string by the GraphQL `Decimal` scalar. */
  dollarAmountSaved?: string | null;
  /** Denormalized provider name; absent until parsed/confirmed. */
  providerName?: string | null;
  /** True once the owner left feedback (reviewed → removed from home). */
  hasFeedback?: boolean | null;
  /** The owner opened the case after it closed — the win no longer needs the hero. */
  closureSeen?: boolean | null;
};

export type ClosedWin = {
  issueId: string;
  title?: string | null;
  caseType?: string | null;
  bucket: WinBucket;
  /** Provider name for the badge; omitted when unknown. */
  providerName?: string;
  /** Dollars saved, parsed from the Decimal string; undefined when 0 / absent. */
  savings?: number;
  /** The owner closed it as resolved themselves — never the hero. */
  userResolved?: true;
};

// Trim + case-fold: the field is free-text, so "resolved " must still match.
const isStaffResolved = (reason: string | null | undefined): boolean =>
  reason != null &&
  RESOLVED_CLOSURE_REASONS.some(
    resolved => resolved.toLowerCase() === reason.trim().toLowerCase(),
  );

/** The owner closed the case themselves and called it resolved. */
export const isUserResolved = (caseItem: ClosedCaseLike): boolean =>
  caseItem.userClosureReason === USER_RESOLVED_CLOSURE_REASON;

const parseSavings = (raw: string | null | undefined): number => {
  if (raw == null) {
    return 0;
  }
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Whether a closed case still qualifies as a home-surfaceable win: positively
 * resolved (by staff or by the owner) and not yet reviewed.
 */
export function isUnreviewedResolution(caseItem: ClosedCaseLike): boolean {
  return (
    (isStaffResolved(caseItem.caseManagementClosureReason) ||
      isUserResolved(caseItem)) &&
    !caseItem.hasFeedback
  );
}

/** Map a qualifying closed case to its `ClosedWin`; null when it isn't a win. */
export function toClosedWin(caseItem: ClosedCaseLike): ClosedWin | null {
  if (!isUnreviewedResolution(caseItem)) {
    return null;
  }
  const savings = parseSavings(caseItem.dollarAmountSaved);
  return {
    issueId: caseItem.id,
    title: caseItem.title,
    caseType: caseItem.caseType,
    bucket: savings > 0 ? "saved" : "verified",
    ...(caseItem.providerName ? { providerName: caseItem.providerName } : {}),
    ...(savings > 0 ? { savings } : {}),
    ...(isUserResolved(caseItem) ? { userResolved: true as const } : {}),
  };
}

const closedAtMs = (caseItem: ClosedCaseLike): number => {
  const ts = caseItem.closedAt ? Date.parse(caseItem.closedAt) : Number.NaN;
  return Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts;
};

/**
 * Pick the single win for the CTA primary slot: the most-recent
 * win, tie-broken by highest savings. Selection is self-contained (sorts on
 * `closedAt`) so it does not depend on the caller's ordering.
 * A win demotes out of the hero once the owner has seen the outcome — either
 * they closed the case themselves (user-resolved) or they opened it after it
 * closed (closureSeen).
 */
export function selectPrimaryWin(
  cases: readonly ClosedCaseLike[],
): ClosedWin | null {
  const candidates = cases.filter(
    caseItem =>
      isUnreviewedResolution(caseItem) &&
      !isUserResolved(caseItem) &&
      !caseItem.closureSeen,
  );
  const [first, ...rest] = candidates;
  if (first === undefined) {
    return null;
  }
  // Most-recent wins; an exact-recency tie breaks toward higher savings.
  const leader = rest.reduce((best, caseItem) => {
    const recency = closedAtMs(caseItem) - closedAtMs(best);
    if (recency > 0) {
      return caseItem;
    }
    if (recency === 0) {
      return parseSavings(caseItem.dollarAmountSaved) >
        parseSavings(best.dollarAmountSaved)
        ? caseItem
        : best;
    }
    return best;
  }, first);
  return toClosedWin(leader);
}
